import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { ReportingLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LOADING_STRING } from '../../../NonReact/constants/Common'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import {
    ALL_VALUE,
    getCallRecordingIntegrations,
    getDefaultReports,
    getPartition,
    getTelephonyServer,
    INVALID_VALUE
} from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'
import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import { SelectOptions } from '../../Common/Components/Selects'
import { StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { DeviceSelectItem, HandleChange, SelectItem, SelectOption } from '../../Common/Components/Selects/Types'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { IndividualTelItem, ReactState, StateSetter, TelSysIntegrationMode } from '../../Common/Types/Types'
import { DEFAULT_CME_ID, SERVER_NONE_VALUE } from '../Constants'
import {
    CALL_RECORDING_NAME,
    CME_PERM_NAME,
    DEVICES_PERM_NAME,
    ENABLE_MFA_COMPONENT_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    ENDPOINT_PARTITION_NAME,
    ENDPOINT_TEL_SYS_NAME,
    ENHANCED_FEATURES_COMPONENT_NAME,
    PARTITION_PERM_NAME,
    READ_ONLY_COMPONENT_NAME,
    REPORT_TEMPLATE_USER_COMPONENT_NAME,
    REPORTING_API_COMPONENT_NAME,
    REPORTING_DEFAULT_REPORTS_COMPONENT_NAME,
    REPORTING_LEVEL_NAME,
    SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME,
    TEL_SYS_PERM_NAME
} from '../FieldNames'
import { Capabilities, PartitionValues, PreferredReportingAccessLevel } from '../Types'
import ReportingAccessLevel, { ReportingAccessLevels } from './ReportingAccessLevels'
import {
    CallRecordingScope,
    resolveCallRecordingDefault,
    resolveCallRecordingScope,
    resolveTelSysToUse
} from './ReportingScope'
import { LicenceResponseMapped } from '../../../NonReact/Types/Types'
import {
    CALL_CONTROL_OPTIONS,
    CALL_RECORDING_DEFAULTS,
    HISTORIC_ONLY,
    INTEGRATION_MODE,
    INTEGRATION_MODE_OPTIONS,
    INTEGRATION_TYPE
} from '../../../NonReact/constants/TelSysConstants'
import { IntegrationType } from '../../TelSysConfiguration/Constants/Constants'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { getCMELicences, getTelSysLicences } from '../../Common/API/Licence/LicenceAPI'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { selectPreferredReportingAccessLevel } from './Utils/selectPreferredReportingAccessLevel'

type Props = {
    callRec?: string
    isLoading: boolean
    viewOnly?: boolean
    isAddMode?: boolean
    setUserDetailsFromDevice: (deviceNumber: string) => void
    setShowSyncPermissionsCheckBox: StateSetter<boolean>
    telSysState: ReactState<Nullable<TelSysTypes>>
    callControlOptionsState?: ReactState<Nullable<string>>
    telSysIntegrationTypeState: ReactState<Nullable<IntegrationType>>
    isTelSysHistoricOnlyState: ReactState<Nullable<boolean>>
    attachedDevice: Nullable<DeviceSelectItem>
    capabilities: Nullable<Capabilities>
    setPermissionReportingLevel: StateSetter<Nullable<string[]>>
    setReportingLevelOptions: StateSetter<Nullable<StandardOptionGroup>[]>
    permissionsReportingLevel: Nullable<string[]>
    preferredReportingAccessLevels?: PreferredReportingAccessLevel[]
    setIsCXIOnlyMode: StateSetter<boolean>
    setTelSysIntegrationMode: StateSetter<Nullable<TelSysIntegrationMode>>
    setTelSysIntegrationModeOptions: StateSetter<Nullable<string>>
    setTelSysPartitionLicences: StateSetter<Nullable<readonly string[]>>
    handleReportingLevelChange: HandleChange
    setReportLevelOptionsLoading: (value: boolean) => void
}

/**
 * Reporting tab content.
 *
 * @param callRec - Call recording value from the user response.
 * @param isLoading - Whether the form is loading.
 * @param viewOnly - Whether the form is view only.
 * @param isAddMode - Whether the form is in add mode.
 * @param setUserDetailsFromDevice - Function to set the user details from the device.
 * @param setUserDetailsFromAgent - Function to set the user details from the agent.
 *
 * @returns The reporting tab content.
 */
const Reporting: FC<Props> = ({
    callRec,
    isLoading,
    viewOnly,
    isAddMode,
    setUserDetailsFromDevice,
    setShowSyncPermissionsCheckBox,
    telSysState: [telSysType, setTelSysType],
    callControlOptionsState,
    telSysIntegrationTypeState: [telSysIntegrationType, setTelSysIntegrationType],
    isTelSysHistoricOnlyState: [isTelSysHistoricOnly, setIsTelSysHistoricOnly],
    attachedDevice,
    capabilities,
    setReportingLevelOptions,
    setPermissionReportingLevel,
    permissionsReportingLevel,
    preferredReportingAccessLevels,
    setIsCXIOnlyMode,
    handleReportingLevelChange,
    setReportLevelOptionsLoading,
    setTelSysIntegrationMode,
    setTelSysIntegrationModeOptions,
    setTelSysPartitionLicences
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ReportingLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()

    const { watch, setValue }: UseFormMethods = useFormContext()

    const { displayError }: MessageContextType = useContext(MessageContext)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const isFirstTime: MutableRefObject<boolean> = useRef<boolean>(true)

    const reportingAccessLevels: ReportingAccessLevel = ReportingAccessLevel.newInstance(language)

    const [callRecordingOptions, setCallRecordingOptions]: ReactState<SelectOptions<string>> = useState<
        SelectOptions<string>
    >([
        {
            value: '',
            label: LOADING_STRING
        }
    ])
    const [availableDefaultReports, setAvailableDefaultReports]: ReactState<Nullable<SelectOptions<string>>> = useState<
        Nullable<SelectOptions<string>>
    >([
        {
            value: SERVER_NONE_VALUE,
            label: LOADING_STRING
        }
    ])
    const [isReadOnlyDisabled, setIsReadOnlyDisabled]: ReactState<boolean> = useState<boolean>(false)
    const [isReportTemplateUserDisabled, setIsReportTemplateUserDisabled]: ReactState<boolean> =
        useState<boolean>(false)

    const [telSys, setTelSys]: ReactState<Nullable<IndividualTelItem>> = useState<Nullable<IndividualTelItem>>()
    const [partition, setPartition]: ReactState<Nullable<PartitionValues>> = useState<Nullable<PartitionValues>>()

    const [licenceSummary, setLicenceSummary]: ReactState<Nullable<LicenceResponseMapped>> =
        useState<Nullable<LicenceResponseMapped>>()

    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)
    const hasReportingLevel: boolean = !IsStringEmpty(reportingLevelValue) && reportingLevelValue !== SERVER_NONE_VALUE

    const readOnlyReportsValue: boolean = watch(READ_ONLY_COMPONENT_NAME)
    const reportTemplateUserValue: boolean = watch(REPORT_TEMPLATE_USER_COMPONENT_NAME)

    const cmeValue: string = watch(CME_PERM_NAME)
    const telSysValue: number = watch(TEL_SYS_PERM_NAME)
    const partitionValue: number[] = watch(PARTITION_PERM_NAME)
    const endpointTelSysValue: number = watch(ENDPOINT_TEL_SYS_NAME)
    const endpointPartitionValue: number = watch(ENDPOINT_PARTITION_NAME)
    const permissionDeviceValue: string = watch(DEVICES_PERM_NAME)

    const isTeamsTelSys: boolean = telSysType === TelSysTypes.MS_TEAMS

    const isEssentialsUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.ESSENTIALS,
        [reportingLevelValue]
    )
    const isProfessionalOrAgentUser: boolean = useMemo(
        (): boolean =>
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT,
        [reportingLevelValue]
    )

    const isCallRecordingEnabled: boolean = useMemo((): boolean => {
        if (IsStringEmpty(userInfo?.Scope?.CallRecIntID) || telSysValue === ALL_VALUE || isTeamsTelSys) return false

        const userPartitions: number[] = userInfo?.Scope?.PartIDInTelSys ?? []
        const isScopedToPartition: boolean = userPartitions.length === 1 && userPartitions[0] !== ALL_VALUE

        if (isScopedToPartition)
            return partition?.PartIDInTelSys != null && userPartitions[0] === partition.PartIDInTelSys

        return true
    }, [
        JSON.stringify(userInfo?.Scope?.CallRecIntID),
        JSON.stringify(userInfo?.Scope?.PartIDInTelSys),
        telSysValue,
        isTeamsTelSys,
        partition?.PartIDInTelSys
    ])

    const hasInitialisedCR: MutableRefObject<boolean> = useRef(false)
    const hasInitialisedReporting: MutableRefObject<boolean> = useRef(false)

    const [isReportingLoading, setIsReportingLoading]: ReactState<boolean> = useState<boolean>(false)

    const handleError = (error: Error): void => {
        if (isMounted.current) {
            setPermissionReportingLevel(undefined)
            setAvailableDefaultReports([
                {
                    value: SERVER_NONE_VALUE,
                    label: language.get(ReportingLanguage.NoReports)
                }
            ])
        }

        displayError(error)
    }

    const handleSyncChange = (value: boolean): void => {
        if (!isFirstTime.current) {
            isFirstTime.current = false
            setValue(SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME, value)
        } else {
            isFirstTime.current = false
        }
    }

    useEffect((): void => {
        setIsReportTemplateUserDisabled(false)

        if (readOnlyReportsValue) {
            setIsReportTemplateUserDisabled(true)
        }
    }, [readOnlyReportsValue])

    useEffect((): void => {
        setIsReadOnlyDisabled(false)

        if (reportTemplateUserValue) {
            setValue(READ_ONLY_COMPONENT_NAME, false)
            setValue(REPORTING_API_COMPONENT_NAME, true)
            setIsReadOnlyDisabled(true)
        }
    }, [reportTemplateUserValue])

    useEffect((): void => {
        if (!hasReportingLevel || !isAddMode) {
            setAvailableDefaultReports(null)
        } else {
            setAvailableDefaultReports([
                {
                    value: SERVER_NONE_VALUE,
                    label: LOADING_STRING
                }
            ])
            getDefaultReports(reportingLevelValue, telSysType, telSysValue)
                .then((defaultReports: Nullable<SelectOptions<string>>): void => {
                    if (isMounted.current && defaultReports) {
                        setAvailableDefaultReports([
                            {
                                value: SERVER_NONE_VALUE,
                                label: language.get(ReportingLanguage.NoReports)
                            },
                            ...defaultReports
                        ])
                        setValue(
                            REPORTING_DEFAULT_REPORTS_COMPONENT_NAME,
                            defaultReports?.find(
                                (defaultReport: SelectOption<string>): Definable<boolean> =>
                                    defaultReport.defaultSelected
                            )?.value ?? ''
                        )
                    }
                })
                .catch(handleError)
        }

        if (reportingLevelValue !== ReportingAccessLevels.ESSENTIALS) {
            if (endpointTelSysValue === ALL_VALUE && telSysValue !== ALL_VALUE) {
                setValue(ENDPOINT_TEL_SYS_NAME, telSysValue)
            }

            if (
                endpointPartitionValue === ALL_VALUE &&
                partitionValue.length === 1 &&
                partitionValue[0] !== ALL_VALUE
            ) {
                setValue(ENDPOINT_PARTITION_NAME, partitionValue[0])
            }
        }

        if (reportingLevelValue === ReportingAccessLevels.ESSENTIALS) {
            if (endpointTelSysValue !== ALL_VALUE && telSysValue === ALL_VALUE) {
                setValue(TEL_SYS_PERM_NAME, endpointTelSysValue)
            }

            if (endpointPartitionValue !== ALL_VALUE && partitionValue.length === 0) {
                setValue(PARTITION_PERM_NAME, endpointPartitionValue)
            }

            setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
            setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
            setValue(ENDPOINT_PARTITION_NAME, ALL_VALUE)
            setValue(ENDPOINT_TEL_SYS_NAME, ALL_VALUE)

            if (hasInitialisedReporting.current) {
                setUserDetailsFromDevice(permissionDeviceValue)
            }
        }
    }, [reportingLevelValue, telSysType])

    useEffect((): void => {
        setLicenceSummary(undefined)

        const setLicenceSummaryProtected = (licenceSummaryLocal: Nullable<LicenceResponseMapped>): void => {
            if (isMounted.current) {
                setLicenceSummary(licenceSummaryLocal)
            }
        }

        if (isStringNotEmpty(cmeValue) && (IsVarEmpty(telSysValue) || telSysValue === ALL_VALUE)) {
            getCMELicences(cmeValue).then(setLicenceSummaryProtected).catch(displayError)
        }

        if (isVarNotEmpty(telSysValue) && telSysValue !== ALL_VALUE) {
            getTelSysLicences(telSysValue).then(setLicenceSummaryProtected).catch(displayError)
        }
    }, [JSON.stringify(permissionsReportingLevel), capabilities, cmeValue, telSysValue, telSysType])

    useEffect((): void => {
        if ((!isAddMode && isLoading) || IsVarEmpty(capabilities)) return

        setReportLevelOptionsLoading(true)
        const currentReportingLevel: string = watch(REPORTING_LEVEL_NAME)

        const newReportingLevelOptions: Nullable<StandardOptionGroup>[] = reportingAccessLevels
            .getReportingLevels(
                capabilities,
                licenceSummary,
                telSysType,
                isTelSysHistoricOnly,
                telSysIntegrationType,
                currentReportingLevel,
                permissionsReportingLevel as ReportingAccessLevels[]
            )
            .filter((option: Nullable<StandardOptionGroup>): boolean => !!option)

        setReportingLevelOptions(newReportingLevelOptions)

        const shouldSetNewValue: boolean = !newReportingLevelOptions.some(
            (reportingGroup: Nullable<StandardOptionGroup>): boolean =>
                !!reportingGroup?.options?.some(
                    (reportingLevel: SelectItem): boolean => reportingLevel.value === currentReportingLevel
                )
        )

        if (permissionsReportingLevel && (shouldSetNewValue || hasInitialisedReporting.current)) {
            const replacementReportingLevel: ReportingAccessLevels = selectPreferredReportingAccessLevel(
                newReportingLevelOptions,
                preferredReportingAccessLevels
            )

            setValue(REPORTING_LEVEL_NAME, replacementReportingLevel)
            handleReportingLevelChange(replacementReportingLevel, currentReportingLevel)

            if (!hasInitialisedReporting.current) hasInitialisedReporting.current = true
        }

        setReportLevelOptionsLoading(false)
    }, [
        JSON.stringify(permissionsReportingLevel),
        capabilities,
        licenceSummary,
        telSysType,
        attachedDevice,
        isTelSysHistoricOnly,
        telSysIntegrationType,
        isLoading,
        isAddMode,
        preferredReportingAccessLevels
    ])

    useEffect((): void => {
        const telSysToUse: Nullable<number> = resolveTelSysToUse(endpointTelSysValue, telSysValue)

        if (!IsVarEmpty(telSysToUse)) {
            let partValue: Nullable<number>

            if (
                endpointPartitionValue &&
                endpointPartitionValue !== ALL_VALUE &&
                endpointPartitionValue !== INVALID_VALUE
            ) {
                partValue = endpointPartitionValue
            } else if (
                partitionValue &&
                partitionValue.length === 1 &&
                partitionValue[0] !== ALL_VALUE &&
                partitionValue[0] !== INVALID_VALUE
            ) {
                partValue = partitionValue[0]
            }

            const telSysFields: string[] = [
                HISTORIC_ONLY,
                INTEGRATION_MODE,
                INTEGRATION_MODE_OPTIONS,
                INTEGRATION_TYPE,
                CALL_RECORDING_DEFAULTS,
                CALL_CONTROL_OPTIONS
            ]

            Promise.all([
                getTelephonyServer(telSysToUse, ...telSysFields),
                partValue ? getPartition(telSysToUse, partValue, 'CallRecordingIDDefault') : undefined
            ])
                .then(([telServer, part]: [Nullable<IndividualTelItem>, Nullable<PartitionValues>]): void => {
                    if (isMounted.current) {
                        setTelSys(telServer)
                        setPartition(part)

                        setTelSysType(telServer?.TelSysType ?? TelSysTypes.UNKNOWN)
                        callControlOptionsState?.[1](telServer?.CallControlOptions ?? null)

                        if (telServer?.TelSysType === TelSysTypes.MS_TEAMS) {
                            setIsTelSysHistoricOnly(telServer?.HistoricOnly)
                        }

                        callControlOptionsState?.[1](telServer?.CallControlOptions ?? null)

                        setIsCXIOnlyMode(telServer?.IntegrationMode === TelSysIntegrationMode.CXI_ONLY)

                        setTelSysIntegrationMode(telServer?.IntegrationMode ?? null)

                        // Null (not undefined) when absent so CXS applicability stops reporting pending.
                        setTelSysIntegrationType(telServer?.IntegrationType ?? null)
                        setTelSysIntegrationModeOptions(telServer?.IntegrationModeOptions ?? null)
                        setTelSysPartitionLicences(telServer?.PartitionLicences ?? null)

                        if (telServer?.ManageUserMFA && isAddMode) {
                            setValue(ENABLE_MFA_COMPONENT_NAME, true)
                        }

                        if (telServer?.LicensedForRptUserType || part?.LicensedForRptUserType) {
                            setPermissionReportingLevel([
                                telServer?.LicensedForRptUserType ??
                                    part?.LicensedForRptUserType ??
                                    ReportingAccessLevels.TWO_THOUSAND
                            ])

                            if (
                                partitionValue.length === 1 &&
                                part &&
                                ((part?.LicensedForRptUserType === ReportingAccessLevels.ENTERPRISE &&
                                    reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR) ||
                                    (part?.LicensedForRptUserType === ReportingAccessLevels.TWO_THOUSAND &&
                                        reportingLevelValue === ReportingAccessLevels.TWO_THOUSAND))
                            ) {
                                setShowSyncPermissionsCheckBox(true)
                                handleSyncChange(part.SynchroniseSupervisorPermissions)
                            } else {
                                setShowSyncPermissionsCheckBox(false)
                                handleSyncChange(false)
                            }
                        } else if (telServer?.PartitionLicences) {
                            setShowSyncPermissionsCheckBox(false)
                            setPermissionReportingLevel(telServer?.PartitionLicences)
                            handleSyncChange(false)
                        } else {
                            setShowSyncPermissionsCheckBox(false)
                            handleSyncChange(false)
                        }
                    }
                })
                .catch(handleError)
        } else {
            setTelSysType(TelSysTypes.UNKNOWN)
            setPermissionReportingLevel(undefined)
            setValue(SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME, false)
            setShowSyncPermissionsCheckBox(false)
        }
    }, [
        telSysValue,
        JSON.stringify(partitionValue),
        endpointTelSysValue,
        endpointPartitionValue,
        JSON.stringify(reportingLevelValue)
    ])

    useEffect((): void => {
        if (isCallRecordingEnabled && hasReportingLevel && !isLoading) {
            if (telSysValue !== INVALID_VALUE && partitionValue[0] !== INVALID_VALUE) {
                const scope: Nullable<CallRecordingScope> = resolveCallRecordingScope(
                    endpointTelSysValue,
                    telSysValue,
                    telSys,
                    partition
                )

                if (IsVarEmpty(scope)) return

                setIsReportingLoading(true)

                getCallRecordingIntegrations(cmeValue ?? DEFAULT_CME_ID, scope.telSysID, scope.partitionID)
                    .then((callRecOptions: SelectOptions<string>): void => {
                        if (isMounted.current) {
                            setCallRecordingOptions(callRecOptions)
                            setIsReportingLoading(false)

                            if (callRec) {
                                setTimeout((): void => {
                                    if (isMounted.current) {
                                        setValue(CALL_RECORDING_NAME, callRec)
                                    }
                                })
                            } else if (isAddMode) {
                                setValue(CALL_RECORDING_NAME, resolveCallRecordingDefault(telSys, partition))
                            }
                        }
                    })
                    .catch(displayError)
            } else {
                setCallRecordingOptions([])
                setIsReportingLoading(false)
            }

            if (!hasInitialisedCR.current && !IsStringEmpty(callRec)) {
                hasInitialisedCR.current = true
            }
        } else if (!isCallRecordingEnabled && telSysValue === ALL_VALUE) {
            setValue(CALL_RECORDING_NAME, SERVER_NONE_VALUE)
        }
    }, [cmeValue, telSys, partition, reportingLevelValue, isLoading, callRec])

    return (
        <div className='reporting-tab tab-area'>
            {hasReportingLevel && (
                <>
                    {availableDefaultReports && isAddMode ? (
                        <FormRow className='default-reports'>
                            <OutlinedSelect
                                name={REPORTING_DEFAULT_REPORTS_COMPONENT_NAME}
                                label={language.get(ReportingLanguage.DefaultReports)}
                                options={availableDefaultReports}
                                disabled={isEssentialsUser || isProfessionalOrAgentUser}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    <FormRow className='warning-row reporting-api'>
                        <InputHelp text={language.get(ReportingLanguage.ReportingApiHint)}>
                            <RouterCheckbox
                                labelLeft={language.get(ReportingLanguage.ReportingApi)}
                                name={REPORTING_API_COMPONENT_NAME}
                                disabled={
                                    viewOnly || reportTemplateUserValue || isEssentialsUser || isProfessionalOrAgentUser
                                }
                            />
                        </InputHelp>
                    </FormRow>
                    <FormRow className='warning-row enhanced-features'>
                        <InputHelp text={language.get(ReportingLanguage.EnhancedFeaturesHint)}>
                            <RouterCheckbox
                                labelLeft={language.get(ReportingLanguage.EnhancedFeatures)}
                                name={ENHANCED_FEATURES_COMPONENT_NAME}
                                disabled={viewOnly || isEssentialsUser || isProfessionalOrAgentUser}
                            />
                        </InputHelp>
                    </FormRow>
                    {isCallRecordingEnabled ? (
                        <FormRow className='call-recording'>
                            <ComboBox
                                variant='outlined'
                                name={CALL_RECORDING_NAME}
                                label={language.get(ReportingLanguage.CallRecording)}
                                loading={isReportingLoading}
                                defaultOption={{
                                    label: language.get(ReportingLanguage.RecordingNone),
                                    value: SERVER_NONE_VALUE
                                }}
                                options={[
                                    ...(callRecordingOptions && callRecordingOptions.length > 0
                                        ? callRecordingOptions
                                        : [{ label: '', value: callRec ?? SERVER_NONE_VALUE }])
                                ]}
                                disabled={viewOnly}
                            />
                        </FormRow>
                    ) : (
                        <></>
                    )}
                    <FormRow className='warning-row read-only'>
                        <InputHelp text={language.get(ReportingLanguage.ReadOnlyHint)}>
                            <RouterCheckbox
                                labelLeft={language.get(ReportingLanguage.ReadOnly)}
                                name={READ_ONLY_COMPONENT_NAME}
                                disabled={
                                    viewOnly || isReadOnlyDisabled || isEssentialsUser || isProfessionalOrAgentUser
                                }
                            />
                        </InputHelp>
                    </FormRow>
                    <FormRow className='warning-row report-template-user'>
                        <InputHelp text={language.get(ReportingLanguage.ReportTemplateUserHint)}>
                            <RouterCheckbox
                                labelLeft={language.get(ReportingLanguage.ReportTemplateUser)}
                                name={REPORT_TEMPLATE_USER_COMPONENT_NAME}
                                disabled={
                                    viewOnly ||
                                    isReportTemplateUserDisabled ||
                                    isEssentialsUser ||
                                    isProfessionalOrAgentUser
                                }
                            />
                        </InputHelp>
                    </FormRow>
                </>
            )}
        </div>
    )
}

export default Reporting
